import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { HERO_IMAGE } from "@/lib/constants";
import { VOLUNTEER } from "@/constants/testIds";
import { CountUp } from "@/components/CountUp";
import { rememberIntent } from "@/lib/intent";
import { api } from "@/lib/api";
import { ArrowRight, Sprout, Award } from "lucide-react";

// Landing hero (parallax blobs, CTAs, live stats). Extracted from
// pages/Landing.jsx in the Phase 15 component split.
// Phase 16 item 3 (2026-07): exactly TWO CTAs — primary "I want to volunteer",
// secondary "We're an organisation". "Start a community" and "Ask a neighbour"
// live in the three-pillar section below; the supporters/donate banner is gone
// from the hero (Donate stays in the footer and /donate).

// Phase 16 item 2 (2026-07): the stats band is social proof, so it only renders
// once the numbers ARE social proof. Below these thresholds nothing renders in
// its place — the hero copy carries the message. Tune here:
export const STATS_MIN_HOURS = 500;
export const STATS_MIN_VOLUNTEERS = 50;

export function HeroSection({ stats }) {
  const { scrollY } = useScroll();
  // Item 10 (2026-07): parallax blobs go static under prefers-reduced-motion.
  const reduce = useReducedMotion();
  const blobY1Raw = useTransform(scrollY, [0, 600], [0, 90]);
  const blobY2Raw = useTransform(scrollY, [0, 600], [0, -70]);
  const blobY1 = reduce ? 0 : blobY1Raw;
  const blobY2 = reduce ? 0 : blobY2Raw;
  const showStats = stats.total_hours >= STATS_MIN_HOURS && stats.volunteers >= STATS_MIN_VOLUNTEERS;
  return (
      <section className="relative overflow-hidden">
        <motion.div style={{ y: blobY1 }} className="pointer-events-none absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full bg-terracotta/10 blur-3xl" />
        <motion.div style={{ y: blobY2 }} className="pointer-events-none absolute top-64 -left-32 w-[420px] h-[420px] rounded-full bg-forest/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-forest/15 bg-white/80 backdrop-blur px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-forest/80">
              <Sprout className="w-3.5 h-3.5" /> Unlocking civic society
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.05] text-forest mt-7">
              Turn time into impact.<br />
              <span className="shine-text">Multiply the good.</span>
            </h1>
            <p className="text-forest/70 leading-relaxed text-lg mt-7 max-w-xl">
              CivicHearth connects everyday people with vetted causes, neighbourhood collectives
              and the neighbours next door. Give an hour, ask for a hand when you need one,
              or bring your street together — every bit of it counts, and gets counted.
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to="/login" onClick={() => rememberIntent("help")}>
                <Button data-testid={VOLUNTEER.ctaSignup} size="lg" className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-8 h-13 h-[52px] text-base shadow-lg shadow-terracotta/20 transition-transform hover:-translate-y-0.5">
                  I want to volunteer <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/partners">
                <Button data-testid={VOLUNTEER.ctaPartner} size="lg" variant="outline" className="rounded-full border-forest/20 text-forest bg-white/70 backdrop-blur hover:bg-white hover:text-forest px-8 h-[52px] text-base transition-transform hover:-translate-y-0.5">
                  We're an organisation
                </Button>
              </Link>
            </div>

            {showStats && (
              <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6" data-testid="hero-stats-band">
                {[
                  { k: stats.total_hours, l: "Hours logged" },
                  { k: stats.volunteers, l: "Volunteers" },
                  { k: stats.organisations, l: "Organisations" },
                  { k: stats.open_opportunities, l: "Open roles" },
                ].map((s, i) => (
                  <div key={s.l} data-testid={`stat-${i}`}>
                    <div className="font-display text-3xl font-semibold text-forest"><CountUp value={s.k} /></div>
                    <div className="text-xs uppercase tracking-[0.18em] text-forest/50 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-3xl overflow-hidden border border-white/50 shadow-2xl shadow-forest/10"
            >
              <img src={HERO_IMAGE} alt="Aerial view down a bunting-lined terraced street with neighbours sharing one long table" loading="eager" className="w-full h-[480px] object-cover" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="absolute -bottom-6 -left-6 hidden sm:block"
            >
              <HeroCelebrationCard live={showStats} />
            </motion.div>
          </div>
        </div>
      </section>
  );
}

/**
 * Phase 17-B item 3 (2026-07): honest hero badge card, threshold-gated.
 * - Live mode (platform above the stats thresholds AND a real acknowledged
 *   celebration exists): "Just earned — {badge} · by {first name}", sourced
 *   from /public/latest-celebration (privacy opt-outs respected server-side).
 * - Explainer mode (default): clearly framed as what members CAN earn —
 *   never implies activity that hasn't happened.
 */
function HeroCelebrationCard({ live }) {
  const [celebration, setCelebration] = useState(null);

  useEffect(() => {
    if (!live) return;
    api.latestCelebration().then(setCelebration).catch(() => setCelebration(null));
  }, [live]);

  const isLive = live && celebration?.has;
  return (
    <div
      className="civic-glass rounded-2xl p-4 w-60 shadow-lg"
      data-testid="hero-celebration-card"
      data-mode={isLive ? "live" : "explainer"}
    >
      <div className="flex items-center gap-3">
        <Award className="w-9 h-9 text-terracotta shrink-0" />
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">
            {isLive ? "Just earned" : "What members earn"}
          </div>
          <div className="font-display text-forest">
            {isLive ? celebration.label : "Community Hero"}
          </div>
          <div className="text-xs text-neutral-500 mt-0.5">
            {isLive ? `by ${celebration.first_name}` : "earned at 50 approved hours"}
          </div>
        </div>
      </div>
    </div>
  );
}
